
import React from "react";
import { Helmet } from "react-helmet";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { PageTitle } from "@/components/PageTitle";
import { CTASection } from "@/components/CTASection";
import { SectionTitle } from "@/components/SectionTitle";
import { FAQAccordion } from "@/components/FAQAccordion";
import { ServiceTestimonials } from "@/components/ServiceTestimonials";
import { Smile } from "lucide-react";

const DentalVeneers: React.FC = () => {
  return (
    <>
      <Helmet>
        <title>Dental Veneers in Nagpur | Cost of Dental Veneers in Nagpur</title>
        <meta
          name="description"
          content="Transform your smile with premium dental veneers at Asrani Dental Clinic in Nagpur. Custom-crafted veneers for a naturally beautiful smile. Book your consultation today!"
        />
      </Helmet>

      <Header />

      <main className="pt-20">
        <PageTitle
          title="Dental Veneers"
        />

        {/* Service Overview Section */}
        <section className="py-16 bg-dental-offwhite">
          <div className="container-custom">
            <div className="flex flex-col md:flex-row gap-12 items-center">
              <div className="flex-1">
                <h2 className="text-3xl font-bold mb-6 text-dental-text">
                  Transform Your Smile with Dental Veneers
                </h2>
                <p className="mb-4">
                  Dental veneers are thin, custom-made shells of tooth-colored materials designed to cover the front surface of teeth, improving their appearance. These wafer-thin coverings are bonded to the front of teeth, changing their color, shape, size, or length.
                </p>
                <p className="mb-4">
                  At Asrani Dental Clinic in Nagpur, we specialize in creating beautiful, natural-looking veneers that can transform your smile while preserving your natural tooth structure. Our expert cosmetic dentists use the highest quality materials and advanced techniques to ensure your veneers look and feel completely natural.
                </p>
                <p className="mb-6">
                  Whether you're looking to fix discolored, worn down, chipped, misaligned, or irregularly shaped teeth, our custom veneers can help you achieve the smile of your dreams with minimal invasiveness.
                </p>
                <div className="flex items-center p-4 bg-white rounded-lg shadow-sm border border-gray-100">
                  <Smile className="w-8 h-8 text-dental-highlight mr-4" />
                  <div>
                    <h3 className="font-semibold">Minimally Invasive Procedure</h3>
                    <p className="text-sm text-gray-600">Our dental veneers require minimal removal of tooth structure while providing maximum aesthetic improvement.</p>
                  </div>
                </div>
              </div>
              <div className="flex-1">
                <img
                  src="/lovable-uploads/dentalveneers.jpg"
                  alt="Dental Veneers"
                  className="rounded-lg shadow-lg w-full"
                />
              </div>
            </div>
          </div>
        </section>

        {/* Treatment Features */}
        <section className="py-16">
          <div className="container-custom">
            <SectionTitle
              title="Our Dental Veneer Options"
              subtitle="Premium Materials for Lasting Results"
            />

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mt-12">
              <div className="bg-white p-6 rounded-lg shadow-md border border-gray-100">
                <div className="w-12 h-12 bg-dental-offwhite rounded-full flex items-center justify-center mb-4">
                  <Smile className="w-6 h-6 text-dental-highlight" />
                </div>
                <h3 className="text-xl font-semibold mb-3">Porcelain Veneers</h3>
                <p>
                  Highly durable and stain-resistant veneers that mimic the light-reflecting properties of natural teeth for the most realistic appearance.
                </p>
              </div>

              <div className="bg-white p-6 rounded-lg shadow-md border border-gray-100">
                <div className="w-12 h-12 bg-dental-offwhite rounded-full flex items-center justify-center mb-4">
                  <Smile className="w-6 h-6 text-dental-highlight" />
                </div>
                <h3 className="text-xl font-semibold mb-3">Composite Veneers</h3>
                <p>
                  Applied directly to the teeth in a single visit, these resin-based veneers offer an affordable option with excellent results and easy repairs.
                </p>
              </div>

              <div className="bg-white p-6 rounded-lg shadow-md border border-gray-100">
                <div className="w-12 h-12 bg-dental-offwhite rounded-full flex items-center justify-center mb-4">
                  <Smile className="w-6 h-6 text-dental-highlight" />
                </div>
                <h3 className="text-xl font-semibold mb-3">Lumineers</h3>
                <p>
                  Ultra-thin veneers that often require no tooth reduction, making them a reversible option for those seeking a less permanent solution.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Why Choose Us */}
        <section className="py-16 bg-dental-offwhite">
          <div className="container-custom">
            <SectionTitle
              title="Why Choose Us for Dental Veneers"
              subtitle="Expert Cosmetic Care at Asrani Dental, Nagpur"
            />

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mt-12">
              <div className="bg-white p-6 rounded-lg shadow-md">
                <h3 className="text-xl font-semibold mb-4 flex items-center">
                  <span className="w-8 h-8 rounded-full bg-dental-highlight flex items-center justify-center text-white mr-3">1</span>
                  Customized Design
                </h3>
                <p>
                  Our dentists work closely with you to design veneers that complement your facial features, skin tone, and personal preferences for a truly custom result.
                </p>
              </div>

              <div className="bg-white p-6 rounded-lg shadow-md">
                <h3 className="text-xl font-semibold mb-4 flex items-center">
                  <span className="w-8 h-8 rounded-full bg-dental-highlight flex items-center justify-center text-white mr-3">2</span>
                  Precision Technology
                </h3>
                <p>
                  We use digital smile design and advanced imaging technology to plan your veneer treatment with precision and show you a preview of your new smile.
                </p>
              </div>

              <div className="bg-white p-6 rounded-lg shadow-md">
                <h3 className="text-xl font-semibold mb-4 flex items-center">
                  <span className="w-8 h-8 rounded-full bg-dental-highlight flex items-center justify-center text-white mr-3">3</span>
                  Conservative Approach
                </h3>
                <p>
                  Our approach focuses on preserving as much natural tooth structure as possible while achieving the desired aesthetic improvements.
                </p>
              </div>

              <div className="bg-white p-6 rounded-lg shadow-md">
                <h3 className="text-xl font-semibold mb-4 flex items-center">
                  <span className="w-8 h-8 rounded-full bg-dental-highlight flex items-center justify-center text-white mr-3">4</span>
                  Quality Materials
                </h3>
                <p>
                  We use only premium-grade porcelain and composite materials that offer excellent durability and the most natural-looking results.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Testimonials Section */}
        <ServiceTestimonials
          testimonials={[
            {
              name: "Priya Mehta",
              comment: "I've always been self-conscious about my slightly discolored and uneven front teeth. Dr. Asrani suggested porcelain veneers, and I couldn't be happier with the results. My smile looks naturally beautiful, and I receive compliments all the time!",
              rating: 5,
              image: "/placeholder.svg"
            },
            {
              name: "Vikram Singh",
              comment: "After years of hiding my smile due to a chipped front tooth, I finally decided to get veneers at Asrani Dental Clinic. The procedure was much more comfortable than I expected, and the results are amazing. I wish I had done this years ago!",
              rating: 5,
              image: "/placeholder.svg"
            }
          ]}
          title="What Our Veneer Patients Say"
          subtitle="Real Experiences from Patients at Asrani Dental Clinic in Nagpur"
        />

        {/* FAQ Section */}
        <section className="py-16 bg-dental-offwhite">
          <div className="container-custom">
            <SectionTitle
              title="Frequently Asked Questions"
              subtitle="Common Questions About Dental Veneers"
            />

            <div className="mt-12 max-w-3xl mx-auto">
              <FAQAccordion
                faqs={[
                  {
                    question: "How long do dental veneers last?",
                    answer: "With proper care, porcelain veneers typically last 10-15 years, while composite veneers usually last 5-7 years. Regular dental check-ups, good oral hygiene, and avoiding habits like nail-biting or using teeth as tools can extend their lifespan."
                  },
                  {
                    question: "Is the procedure painful?",
                    answer: "The veneer procedure is generally painless. We use local anesthesia when preparing the teeth, and most patients report minimal to no discomfort. Any mild sensitivity after the procedure typically subsides within a few days."
                  },
                  {
                    question: "How many appointments are needed for veneers?",
                    answer: "Porcelain veneers typically require 2-3 appointments: consultation and planning, tooth preparation and impressions, and the final placement. Composite veneers can often be completed in a single visit. We'll provide a specific timeline during your consultation."
                  },
                  {
                    question: "Do veneers stain?",
                    answer: "Porcelain veneers are highly resistant to staining and maintain their color for many years. Composite veneers are more susceptible to staining over time but can be polished to restore their appearance. We recommend limiting consumption of staining foods and beverages like coffee, tea, and red wine."
                  },
                  {
                    question: "Are veneers reversible?",
                    answer: "Traditional porcelain veneers require some enamel removal, making them irreversible. However, we offer ultra-thin options like Lumineers that require minimal or no tooth preparation and are potentially reversible. We'll discuss all options during your consultation."
                  }
                ]}
              />
            </div>
          </div>
        </section>

        <CTASection
          title="Ready for Your Smile Transformation?"
          description="Schedule your consultation with Asrani Dental Clinic in Nagpur to discover how dental veneers can enhance your smile."
          primaryButtonText="Book Consultation"
          primaryButtonLink="/dental-appointment-booking"
        />
      </main>

      <Footer />
    </>
  );
};

export default DentalVeneers;
